import axios from 'axios';
import { Service } from 'axios-middleware';
import { get, groupBy, transform, uniq } from 'lodash';
import { normalize } from 'normalizr';

import {
    domainAggResultSchema,
    senderAggResultSchema,
} from './schemas/aggregate-results-schemas';

import { aggDomainsSchema } from './schemas/aggregate-domains-schemas';

import {
    domainGroupSchema,
    domainGroupsSchema,
} from './schemas/domain-groups-schemas';

import { domainsSchema, domainNotesSchema } from './schemas/domains-schemas';

import { suggestedDomainsSchema } from './schemas/suggested-domains-schemas';

import { domainsBySenderSchema, sendersByDomainSchema } from './schemas/recognized-senders-schemas';

import { getJwtCookie } from '../../utils/cookie-utils';

import { formatQueryKey, validateHttpMethod } from './service-utilities';

import {
    DOMAIN_STATUS_APPROVED,
    DOMAIN_STATUS_SUPPRESSED,
    REQUEST_GET,
    REQUEST_POST,
    REQUEST_PUT,
    REQUEST_DELETE,
} from '../constants/definitions';

const availableParams = {
    '/v1/actions': [ 'domain', 'groupId', 'limit', 'senderId', 'severity' ],
    '/v1/aggregate-results': [ 'dataSource', 'domain', 'domainGroupId', 'previousDays', 'senderId', 'limit' ],
    '/v1/domain-groups': [],
    '/v1/domains': [ 'search', 'matchPos', 'dmarcPolicy', 'dmarcStatus', 'domainType', 'domainGroupId', 'domainStatus' ],
    '/v1/recognized-senders': ['status', 'domain', 'domainGroupId', 'senderId'],
    '/v1/reports/aggregate-domains': [ 'previousDays' ],
    '/v1/reports/chart-image': [ 'startDate', 'endDate', 'interval', 'domainType', 'domain' ],
    '/v1/reports/dmarc-authentication': [ 'startDate', 'endDate', 'interval', 'domainType', 'domain' ],
    '/v1/reports/dmarc-coverage-domains': [ 'startDate', 'endDate', 'interval', 'domainType', 'domain' ],
    '/v1/reports/dmarc-coverage-messages': [ 'startDate', 'endDate', 'interval', 'domainType', 'domain' ],
    '/v1/reports/dmarc-effectiveness': [ 'startDate', 'endDate', 'interval', 'domainType', 'domain' ],
    '/v1/reports/pdf': [ 'startDate', 'endDate', 'interval', 'domainType', 'domain' ],
    '/v1/senders/': [],
};

export default class MimirService{

    constructor( baseURL, middlewareConfig = false ){
        const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        };

        const jwtCookie = getJwtCookie();

        if( jwtCookie ){
            headers.Authorization = `Bearer ${jwtCookie}`;
        }

        this.instance = axios.create( {
            baseURL,
            headers
        } );

        if( middlewareConfig ){
            const middleware = new Service( this.instance );
            middleware.register( middlewareConfig );
        }
    }

    getActions( params ){
        return this.request( '/v1/actions', { params } )
            .then( ( response ) => {
                const groupedActions = groupBy( response.data, 'domainName' );
                return groupedActions;
            } );
    }

    getAggregateData( params = { dataSource: 'consumer', previousDays: '7' } ){
        return this.request( '/v1/aggregate-results', { params } )
            .then( ( response ) => {
                const bySender = normalize( response.data, senderAggResultSchema );
                const byDomain = normalize( response.data, domainAggResultSchema );
                return {
                    aggResultsByDomain: byDomain.entities.aggResultsByDomain || {},
                    aggResultsBySender: bySender.entities.aggResultsBySender || {},
                    allDomainIds: uniq( byDomain.result ) || [],
                    allSenderIds: uniq( bySender.result ) || [],
                };
            } );
    }

    // Domains
    getAggregateDomains( params = { previousDays: '7' } ){
        return this.request( '/v1/reports/aggregate-domains', { params } )
            .then( ( response ) => {
                const normalizedAggDomains = normalize( response.data, aggDomainsSchema );
                return {
                    allIds: normalizedAggDomains.result,
                    byId: get( normalizedAggDomains, 'entities.aggDomains', {} ),
                };
            } );
    }

    getApprovedDomains( params ){
        return this.request( '/v1/domains', { params: { ...params, domainStatus: DOMAIN_STATUS_APPROVED } } )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data, domainsSchema );

                return {
                    allIds: normalizedDomains.result,
                    domainsById: normalizedDomains.entities.domains,
                };
            } );
    }

    getDomainsWithCounts( params ){
        return this.request( '/v1/domains-management', { params } )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data, domainsSchema );
                const pagination = get( response, 'metadata.pagination', {} );
                return {
                    allIds: normalizedDomains.result,
                    byId: get( normalizedDomains, 'entities.domains', {} ),
                    pagination,
                };
            } );
    }

    getDomainsOverview(){
        return this.request( '/v1/domains/overview' )
            .then( ( response ) => {
                return response.data;
            } );
    }

    // Suggested Domains
    getSuggestedDomains( params ){
        return this.request( '/v1/suggested-domains', { params } )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data, suggestedDomainsSchema );
                const pagination = get( response, 'metadata.pagination', {} );

                return {
                    allIds: normalizedDomains.result,
                    byId: get( normalizedDomains, 'entities.domains', {} ),
                    pagination,
                };
            } );
    }
    getSuggestedDomainsOrgDomains( params ){
        return this.request( '/v1/suggested-domains/org-domains', { params } )
            .then( ( response ) => {
                return {
                    orgDomains: response.data
                };
            } );
    }

    // Domain Groups
    getDomainGroups(){
        return this.request( '/v1/domain-groups' )
            .then( ( response ) => {
                const normalizedDomainGroups = normalize( response.data, domainGroupsSchema );
                return {
                    allDomainGroupIds: normalizedDomainGroups.result,
                    domainGroupsById: get( normalizedDomainGroups, 'entities.domainGroups', {} ),
                };
            } );
    }

    createDomainGroup( { name, description, domains } ){

        if( !description ){
            description = '';
        }

        const sanitizedGroup = {
            description: description,
            domains: domains,
            name: name,
        };
        return this.request( '/v1/domain-groups', sanitizedGroup, REQUEST_POST )
            .then( ( response ) => {
                const normalizedGroups = normalize( response.data, domainGroupSchema );
                return {
                    allIds: [normalizedGroups.result],
                    domainGroupsById: normalizedGroups.entities.domainGroups,
                };
            } );
    }

    updateDomainGroup( { name, description, domains = [], id } ){

        if( !description ){
            description = '';
        }

        const sanitizedGroup = {
            description: description,
            domains: domains,
            id: id,
            name: name,
        };
        return this.request( `/v1/domain-groups/${id}`, sanitizedGroup, REQUEST_PUT )
            .then( ( response ) => {
                const normalizedGroups = normalize( response.data, domainGroupSchema );
                return {
                    allIds: [normalizedGroups.result],
                    domainGroupsById: normalizedGroups.entities.domainGroups,
                };
            } );
    }

    deleteDomainGroup( groupId ){
        return this.request( `/v1/domain-groups/${groupId}`, {}, REQUEST_DELETE )
            .then( ( response ) => {
                return response;
            } );
    }

    deleteGroupsFromDomains( domain ){
        return this.request( `/v1/domains/${domain}/domain-groups`, { }, REQUEST_DELETE )
            .then( ( response ) => {
                return response;
            } );
    }

    // Domain Notes
    getDomainNotes( domain ){
        return this.request( `/v1/domains/${domain}/notes` )
            .then( ( response ) => {
                const normalizedDomainNotes = normalize( response.data, domainNotesSchema );
                return {
                    allIds: normalizedDomainNotes.result,
                    byId: get( normalizedDomainNotes, 'entities.notes', {} ),
                };
            } );
    }

    postDomainNotes( domain, data ){
        return this.request( `/v1/domains/${domain}/notes`, { note: data }, REQUEST_POST )
            .then( ( response ) => {
                return response.data;
            } );
    }

    // Recognized Senders
    getRecognizedSenders( domain, senderId, domainGroupId, status ){
        const params = {
            domain,
            domainGroupId,
            senderId,
            status,
        };

        return this.request( '/v1/recognized-senders', { params } )
            .then( ( response ) => {
                const recognizedSendersSchema = domain ? domainsBySenderSchema : sendersByDomainSchema;
                const normalized = normalize( response.data, recognizedSendersSchema );

                return {
                    byId: normalized.entities.senderDomainPair,
                };
            } );
    }

    getSender( senderId ){
        return this.request( `/v1/senders/${senderId}` )
            .then( response => {
                return response.data;
            } );
    }

    updateRecognizedSenders( domain, senderData ){
        return this.instance.post( '/v1/recognized-senders', senderData )
            .then( ( response ) => {
                const recognizedSendersSchema = domain ? domainsBySenderSchema : sendersByDomainSchema;
                const normalized = normalize( response.data.data, recognizedSendersSchema );

                return {
                    byId: normalized.entities.senderDomainPair,
                };
            } );
    }

    // Bulk Domain Management
    addDomainsBulk = ( domains = [], domainType ) => {
        const data = domains.map( domain => ( { domain, domainStatus: DOMAIN_STATUS_APPROVED, domainType } ) );

        return this.instance.post( `/v1/domains`, data )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data.data, domainsSchema );
                return {
                    allIds: normalizedDomains.result,
                    data: response.data,
                    domainsById: get( normalizedDomains, 'entities.domains', {} ),
                    status: response.status,
                };
            } );
    }

    editDomainsBulk = ( domains = [], domainType ) => {
        const data = domains.map( domain => ( { domain, domainStatus: DOMAIN_STATUS_APPROVED, domainType } ) );

        return this.instance.put( `/v1/domains`, data )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data.data, domainsSchema );
                return {
                    allIds: normalizedDomains.result,
                    data: response.data,
                    domainsById: normalizedDomains.entities.domains,
                    status: response.status,
                };
            } );
    }

    removeDomainsBulk = ( domains = [] ) => {
        const data = domains.map( domain => ( { domain, domainStatus: DOMAIN_STATUS_SUPPRESSED } ) );

        return this.instance.put( `/v1/domains`, data )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data.data, domainsSchema );
                return {
                    allIds: normalizedDomains.result,
                    data: response.data,
                    domainsById: normalizedDomains.entities.domains,
                    status: response.status,
                };
            } );
    }

    // Suggested Domain Management
    claimSuggestedDomains = ( domains = [], domainType ) => {
        const data = domains.map( domain => ( { domain, domainStatus: DOMAIN_STATUS_APPROVED, domainType } ) );

        return this.instance.post( `/v1/suggested-domains`, data )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data.data, domainsSchema );
                return {
                    allIds: normalizedDomains.result,
                    data: response.data,
                    domainsById: normalizedDomains.entities.domains,
                    status: response.status,
                };
            } );
    }

    removeSuggestedDomains = ( domains = [] ) => {
        const data = domains.map( domain => ( { domain, domainStatus: DOMAIN_STATUS_SUPPRESSED, domainType: 'nonsending' } ) );

        return this.instance.post( `/v1/suggested-domains`, data )
            .then( ( response ) => {
                const normalizedDomains = normalize( response.data.data, domainsSchema );
                return {
                    allIds: normalizedDomains.result,
                    data: response.data,
                    domainsById: normalizedDomains.entities.domains,
                    status: response.status,
                };
            } );
    }

    getWhois( domain = '' ){
        return this.request( `/v1/whois/domain/${domain}` )
            .then( ( response ) => response.data );
    }

    request( path, config = { params: {} }, httpMethod = REQUEST_GET ){
        validateHttpMethod( httpMethod );
        let snakeCaseParams;

        const supportedQueryParams = availableParams[path] || [];

        if( config.params ){
            snakeCaseParams = transform( config.params, ( result, value, key ) => {

                if( supportedQueryParams.includes( key ) || supportedQueryParams.length === 0 ){


                    let correctKey = formatQueryKey( key );
                    result[correctKey] = value;
                }
            } );
        }

        return this.instance[httpMethod]( path, { ...config, params: snakeCaseParams } )
            .then( ( response ) => {
                response.metadata = response.data.metadata;
                response.data = response.data.data;

                return response;
            } );
    }
}
